import { defineStore } from 'pinia';
import { computed, onMounted, onUnmounted, ref } from 'vue';

import * as Sentry from '@sentry/vue';

import Cache from '@/helpers/cache';
import { capitalize } from '@/helpers/string';
import { AUTH_STATE_KEY, SELECTED_AGENCY_KEY } from '@/modules/auth/constants';
import {
	getTokenExpiration,
	isTokenExpired,
} from '@/modules/auth/helpers/jwtUtils';
import { ensureFreshToken } from '@/modules/auth/helpers/tokenRefresh';
import authService from '@/modules/auth/services/authService';
import { useRegistrationsStore } from '@/store/registrationsStore';
import { type IUserDB, UserRole } from '@/types/user';

import type { KeycloakAgency, LoginResponse } from '@/modules/auth/types';
import type { IAgencyDB } from '@/types/agency';

// Refresh token 60s before it expires, with a minimum delay of 10s
const REFRESH_BEFORE_EXPIRY_MS = 60_000;
const MIN_REFRESH_DELAY_MS = 10_000;

interface Collaborator {
	id: string;
	firstname: string;
	lastname: string;
	email: string;
	username: string;
	phone: string;
	roles: UserRole[];
	agencyId: string;
	agenciesPopulated: KeycloakAgency[];
}

export const useAuthStore = defineStore('auth', () => {
	// State
	const token = ref<string | null>(
		authService.getTokens()?.access_token ?? null
	);
	const error = ref<string | null>(null);
	const user = ref<IUserDB | null>(null);
	const currentAgency = ref<IAgencyDB | null>(null);
	const supervisorAgencyId = ref<string>('');
	const collaborators = ref<Collaborator[]>([]);
	const selectedCollaborators = ref<string[]>(['all']);
	const selectedDomains = ref<string[]>(['all']);
	const logasAgencyId = ref(localStorage.getItem('logas_agency_id') || '');
	const isLoading = ref(false);
	const timeout = ref<number | null>(null);

	// Deterministic user-ready signal: resolves after the initial userInfo
	// fetch completes (true if user loaded, false otherwise). Replaces the
	// previous setTimeout polling in waitUser which could miss the update.
	let resolveUserReady: ((value: boolean) => void) | null = null;
	const userReadyPromise = new Promise<boolean>((resolve) => {
		resolveUserReady = resolve;
	});

	const scheduleRefresh = () => {
		if (timeout.value) {
			clearTimeout(timeout.value);
		}

		const tokens = authService.getTokens();
		if (!tokens) return;

		const exp = getTokenExpiration(tokens.access_token);
		if (!exp) return;

		const delay = Math.max(
			exp - Date.now() - REFRESH_BEFORE_EXPIRY_MS,
			MIN_REFRESH_DELAY_MS
		);

		timeout.value = setTimeout(async () => {
			try {
				const freshTokens = authService.getTokens();
				if (!freshTokens) return;

				if (
					!isTokenExpired(freshTokens.access_token, REFRESH_BEFORE_EXPIRY_MS)
				) {
					scheduleRefresh();
					return;
				}

				const freshAccessToken = await ensureFreshToken();
				token.value = freshAccessToken;
				scheduleRefresh();
			} catch {
				// ensureFreshToken handles forceLogout on failure
			}
		}, delay);
	};

	const handleStorageChange = (event: StorageEvent) => {
		if (event.key !== AUTH_STATE_KEY) return;

		if (!event.newValue) {
			logoutSuccess();
			return;
		}

		try {
			const newTokens = JSON.parse(event.newValue);
			if (newTokens.access_token && newTokens.access_token !== token.value) {
				token.value = newTokens.access_token;
				scheduleRefresh();
			}
		} catch {
			// Malformed JSON in storage — ignore
		}
	};

	const handleVisibilityChange = async () => {
		if (document.visibilityState !== 'visible') return;

		const tokens = authService.getTokens();
		if (!tokens) return;

		if (isTokenExpired(tokens.access_token, 30_000)) {
			try {
				const freshAccessToken = await ensureFreshToken();
				token.value = freshAccessToken;
				scheduleRefresh();
			} catch {
				// ensureFreshToken handles forceLogout on failure
			}
		} else {
			scheduleRefresh();
		}
	};

	onMounted(async () => {
		isLoading.value = true;
		try {
			const tokens = authService.getTokens();
			if (tokens) {
				const fetchedUser = await authService.fetchUserInfo(
					tokens.access_token
				);
				setUser(fetchedUser);
				scheduleRefresh();
				resolveUserReady?.(true);
			} else {
				resolveUserReady?.(false);
			}
		} catch (error) {
			console.error(error);
			resolveUserReady?.(false);
		} finally {
			isLoading.value = false;
		}

		window.addEventListener('storage', handleStorageChange);
		document.addEventListener('visibilitychange', handleVisibilityChange);
	});

	onUnmounted(() => {
		if (timeout.value) {
			clearTimeout(timeout.value);
		}
		window.removeEventListener('storage', handleStorageChange);
		document.removeEventListener('visibilitychange', handleVisibilityChange);
	});

	// Getters
	const currentUser = computed(() => user.value);

	const userFullName = computed(() => {
		if (!user.value) return '';
		return `${capitalize(user.value.firstname)} ${capitalize(user.value.lastname)}`;
	});

	const userFirstName = computed(() => {
		if (!user.value) return '';
		return capitalize(user.value.firstname);
	});

	const userPositions = computed(() => user.value?.roles);

	const userPicture = computed(() => user.value?.picture);

	const currentUserId = computed(() => user.value?.id);

	const agencyId = computed(() => user.value?.agencyId);

	const isSupervisor = computed(
		() => user.value?.roles?.includes(UserRole.SUPERVISOR) ?? false
	);

	const isSupervisorOnly = computed(
		() => isSupervisor.value && !supervisorAgencyId.value
	);

	const isManager = computed(
		() =>
			isAgency.value && (user.value?.roles?.includes(UserRole.MANAGER) ?? false)
	);

	const isCollaborator = computed(
		() =>
			isAgency.value &&
			(user.value?.roles?.includes(UserRole.COLLABORATOR) ?? false)
	);

	const isCompany = computed(
		() => user.value?.roles?.includes(UserRole.COMPANY) ?? false
	);

	const isAgency = computed(() => {
		return (
			!!logasAgencyId.value ||
			(user.value?.roles?.includes(UserRole.SUPERVISOR) &&
				!!supervisorAgencyId.value) ||
			user.value?.roles?.includes(UserRole.MANAGER) ||
			user.value?.roles?.includes(UserRole.COLLABORATOR)
		);
	});

	const supervisorAgencies = computed(() => {
		if (isSupervisor.value && user.value?.agenciesPopulated) {
			return user.value.agenciesPopulated;
		}
		return [];
	});

	const userContext = computed(() => (isAgency.value ? 'agency' : 'company'));

	const currentCollaborator = computed(() => user.value);

	// Always Interaction agency now, evolia replaces contractManagerIdentifier
	const isInteraction = computed(() => isAgency.value);

	const isInteractionLimited = computed(
		() =>
			isInteraction.value &&
			import.meta.env.VITE_LIMIT_INTERACTION_UPDATE === 'true'
	);

	// Features are always enabled now
	const hasATS = computed(() => isAgency.value);

	const hasSalesforce = computed(() => isAgency.value);

	const hasJobOffers = computed(() => isAgency.value);

	const collaboratorsOptions = computed(() => {
		return collaborators.value.map((collab: Collaborator) => ({
			label: `${capitalize(collab.firstname)} ${capitalize(collab.lastname)}`,
			value: collab.id,
		}));
	});

	const getCollaborator = (collaboratorId: string) => {
		return (collaborators.value || []).find(
			(c: Collaborator) => c.id === collaboratorId
		);
	};

	// Actions
	const loginSuccess = (loginResponse: LoginResponse) => {
		authService.setTokens({ ...loginResponse, issued_at: Date.now() });
		token.value = loginResponse.access_token;
		scheduleRefresh();
		error.value = null;
		if (import.meta.env.VITE_ENV === 'local') {
			const cache = new Cache('local');
			cache.clear();
		}
	};

	const setUser = (userData: IUserDB) => {
		user.value = userData;
		Sentry.setUser({ id: userData.id, email: userData.email });
	};

	const setAgency = (agency: IAgencyDB) => {
		currentAgency.value = agency;
		// Persist selected agency ID
		if (agency.id) {
			localStorage.setItem(SELECTED_AGENCY_KEY, agency.id.toString());
		}
	};

	const getSelectedAgencyId = (): string | null => {
		return localStorage.getItem(SELECTED_AGENCY_KEY);
	};

	const setSupervisorAgency = (agency: IAgencyDB) => {
		supervisorAgencyId.value = agency.id?.toString() ?? '';
		if (user.value) {
			user.value = { ...user.value, agencyId: agency.id };
		}
	};

	const setLogasAgency = (agencyId: string) => {
		logasAgencyId.value = agencyId;
		localStorage.setItem('logas_agency_id', agencyId);
	};

	const setCollaborators = (collabs: Collaborator[]) => {
		collaborators.value = collabs;
	};

	const logoutSuccess = () => {
		useRegistrationsStore().stopPolling();
		token.value = null;
		error.value = null;
		user.value = null;
		currentAgency.value = null;
		supervisorAgencyId.value = '';
		collaborators.value = [];
		selectedCollaborators.value = ['all'];
		selectedDomains.value = ['all'];
		logasAgencyId.value = '';
		localStorage.removeItem('logas_agency_id');
		authService.removeTokens();
		localStorage.removeItem(SELECTED_AGENCY_KEY);
		sessionStorage.clear();
		Sentry.setUser(null);
	};

	const waitUser = (): Promise<boolean> => userReadyPromise;

	return {
		// State (exposed as refs for reactivity)
		token,
		error,
		user,
		currentAgency,
		supervisorAgencyId,
		logasAgencyId,
		collaborators,
		selectedCollaborators,
		selectedDomains,
		isLoading,
		// Getters (computed)
		currentUser,
		userFullName,
		userFirstName,
		userPositions,
		userPicture,
		currentUserId,
		agencyId,
		isSupervisor,
		isSupervisorOnly,
		isManager,
		isCollaborator,
		isCompany,
		isAgency,
		supervisorAgencies,
		userContext,
		currentCollaborator,
		isInteraction,
		isInteractionLimited,
		hasATS,
		hasSalesforce,
		hasJobOffers,
		collaboratorsOptions,
		getCollaborator,

		// Actions
		scheduleRefresh,
		loginSuccess,
		setUser,
		setAgency,
		getSelectedAgencyId,
		setSupervisorAgency,
		setLogasAgency,
		setCollaborators,
		logoutSuccess,
		waitUser,
	};
});
